import React from 'react';
import PropTypes from 'prop-types';
import MuiButton from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import { palette } from './theme';

// Estados reais de hover/active por classe (JSS) — usar `style` inline não permite hover,
// porque o estado :hover do MUI fica por baixo da cor sobrescrita via style.
const styles = {
  dark: {
    backgroundColor: palette.grey[900],
    color: '#ffffff',
    '&:hover': { backgroundColor: '#000000' },
    '&:active': { backgroundColor: '#000000' },
  },
  light: {
    backgroundColor: palette.grey[100],
    color: palette.text.primary,
    '&:hover': { backgroundColor: palette.grey[200] },
    '&:active': { backgroundColor: palette.grey[300] },
  },
  outlined: {
    borderColor: palette.grey[300],
    color: palette.text.primary,
    '&:hover': { backgroundColor: palette.grey[50], borderColor: palette.grey[400] },
    '&:active': { backgroundColor: palette.grey[100] },
  },
  danger: {
    backgroundColor: palette.error.main,
    color: '#ffffff',
    '&:hover': { backgroundColor: '#a53024' },
    '&:active': { backgroundColor: '#8c291f' },
  },
};

const INTENT_PROPS = {
  // Ação principal da tela (ex.: Salvar, Adicionar) — preto sólido
  primary: { variant: 'contained' },
  // Ação secundária neutra (ex.: Cancelar, Fechar) — cinza claro
  secondary: { variant: 'contained' },
  // Ação destrutiva (ex.: Excluir)
  danger: { variant: 'contained' },
  // Ação de texto sem fundo (ex.: dentro de uma linha de tabela)
  text: { variant: 'text', color: 'primary' },
  // Alias explícito do preto sólido
  dark: { variant: 'contained' },
  // Alias explícito do cinza claro
  light: { variant: 'contained' },
  // Ação terciária/menos urgente (ex.: Filtrar, Exportar) — só contorno
  outlined: { variant: 'outlined' },
};

// intents que mapeiam pra uma classe custom (cor não vem do theme palette, vem do JSS acima)
const CLASS_BY_INTENT = {
  primary: 'dark',
  secondary: 'light',
  danger: 'danger',
  dark: 'dark',
  light: 'light',
  outlined: 'outlined',
};

// Botão com variantes semânticas fixas e hover/active reais — substitui a combinação livre de
// variant+color repetida em cada view. Uso: <Button intent="primary">Salvar</Button> (preto),
// <Button intent="secondary">Cancelar</Button> (cinza claro), <Button intent="outlined">Filtrar</Button>,
// <Button intent="danger">Excluir</Button>, <Button intent="text">Editar</Button>
const Button = ({ intent, classes, className, ...rest }) => {
  const intentProps = INTENT_PROPS[intent] || INTENT_PROPS.primary;
  const intentClassName = classes[CLASS_BY_INTENT[intent]];
  const finalClassName = [intentClassName, className].filter(Boolean).join(' ');

  return <MuiButton {...intentProps} className={finalClassName} {...rest} />;
};

Button.propTypes = {
  intent: PropTypes.oneOf(['primary', 'secondary', 'danger', 'text', 'dark', 'light', 'outlined']),
  className: PropTypes.string,
};

Button.defaultProps = {
  intent: 'primary',
};

export default withStyles(styles)(Button);
